// Permissões V2 — UI mínima para gestão de grupos + permissões individuais
import React, { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Save, Plus, Trash2, Users, Shield, History, RefreshCw, AlertTriangle } from 'lucide-react';
import { usePermV2 } from '@/lib/perm_v2';

const VERB_LABELS = {
  visualizar: 'Ver', criar: 'Criar', editar: 'Editar', excluir: 'Excluir',
  exportar: 'Exportar', importar: 'Importar', enviar: 'Enviar', imprimir: 'Imprimir',
  configurar: 'Config', aprovar: 'Aprovar', cancelar: 'Cancelar', reverter: 'Reverter',
};

const GRUPO_LABELS = {
  admin: 'Núcleo', operacional: 'Operacional', financeiro: 'Financeiro',
  pedagogico: 'Pedagógico', dashboard: 'Dashboards',
};

export default function PermissoesV2() {
  const { perms: meuPerm, refresh: refreshMe } = usePermV2();
  const [registry, setRegistry] = useState(null);
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('grupos'); // grupos | usuarios | audit
  const [editingGroup, setEditingGroup] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [creatingGroup, setCreatingGroup] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [reg, gs, us, au] = await Promise.all([
        api.get('/perm/registry'),
        api.get('/perm/groups'),
        api.get('/users'),
        api.get('/perm/audit?limit=50'),
      ]);
      setRegistry(reg.data);
      setGroups(gs.data.items || []);
      setUsers(Array.isArray(us.data) ? us.data : (us.data?.items || []));
      setAudit(au.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao carregar permissões.');
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  if (loading || !registry) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-purple-600" /></div>;
  }

  const modules = registry.modules || {};
  const moduleEntries = Object.entries(modules);
  const grupos = [...new Set(moduleEntries.map(([, c]) => c.grupo))];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-purple-700"><Shield className="w-5 h-5" />Permissões V2</CardTitle>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={loadAll} data-testid="perm-reload"><RefreshCw className="w-4 h-4 mr-1" />Recarregar</Button>
              {meuPerm?.is_admin_bypass && (
                <Button size="sm" onClick={() => setCreatingGroup(true)} data-testid="perm-new-group"><Plus className="w-4 h-4 mr-1" />Novo Grupo</Button>
              )}
            </div>
          </div>
          <div className="flex gap-2 mt-3 border-b">
            {[['grupos', `Grupos (${groups.length})`, Users], ['usuarios', `Usuários (${users.length})`, Users], ['audit', 'Auditoria', History]].map(([k, label, Icon]) => (
              <button key={k} onClick={() => setTab(k)} className={`px-3 py-2 text-sm border-b-2 ${tab === k ? 'border-purple-600 text-purple-700 font-semibold' : 'border-transparent text-slate-600'}`} data-testid={`perm-tab-${k}`}>
                <Icon className="w-4 h-4 inline mr-1" />{label}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {tab === 'grupos' && (
            <div className="space-y-2">
              {groups.map((g) => (
                <div key={g.id} className="border rounded p-3 flex items-center justify-between" data-testid={`perm-group-${g.id}`}>
                  <div>
                    <div className="font-semibold text-slate-800">{g.nome} {g.system && <span className="text-[10px] ml-1 px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">SISTEMA</span>}</div>
                    <div className="text-xs text-slate-500">{g.descricao || '—'}</div>
                    <div className="text-[11px] text-emerald-700 mt-1">{Object.values(g.acoes_v2 || {}).filter(v => v).length} permissões ativas</div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEditingGroup(g)} data-testid={`perm-edit-group-${g.id}`}>Editar</Button>
                    {!g.system && (
                      <Button size="sm" variant="outline" onClick={async () => {
                        if (!window.confirm(`Excluir o grupo "${g.nome}"?`)) return;
                        try { await api.delete(`/perm/groups/${g.id}`); toast.success('Excluído.'); await loadAll(); }
                        catch (e) { toast.error(e?.response?.data?.detail || 'Falha.'); }
                      }}><Trash2 className="w-3.5 h-3.5 text-rose-600" /></Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === 'usuarios' && (
            <div className="space-y-1">
              {users.map((u) => (
                <div key={u.id} className="border rounded p-2 flex items-center justify-between text-sm" data-testid={`perm-user-${u.id}`}>
                  <div>
                    <div className="font-medium">{u.name} <span className="text-slate-400 text-xs ml-1">{u.email}</span></div>
                    <div className="text-[11px] text-slate-500">role: {u.role}{u.role === 'admin' && <span className="ml-2 text-amber-600 font-semibold">⚠ ADMIN bypass</span>}{u.custom_profile_id && <span className="ml-2 text-purple-600">· grupo: {(groups.find(g => g.id === u.custom_profile_id) || {}).nome || u.custom_profile_id}</span>}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setEditingUser(u)} data-testid={`perm-edit-user-${u.id}`}>Permissões</Button>
                </div>
              ))}
            </div>
          )}

          {tab === 'audit' && (
            <div className="space-y-1 text-xs">
              {audit.length === 0 && <div className="text-slate-500 italic">Sem registros.</div>}
              {audit.map((a) => (
                <div key={a.id} className="border rounded p-2" data-testid={`perm-audit-${a.id}`}>
                  <div className="flex justify-between">
                    <span className="font-mono text-[10px] text-slate-600">{new Date(a.at).toLocaleString('pt-BR')}</span>
                    <span className="px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 text-[10px] font-bold">{a.target_type.toUpperCase()}</span>
                  </div>
                  <div className="mt-1"><strong>{a.actor_email}</strong> · {a.comment || '—'}</div>
                  <div className="text-slate-500">target: {a.target_id}</div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {(creatingGroup || editingGroup) && (
        <GroupEditor
          modules={modules}
          group={editingGroup}
          creating={creatingGroup}
          allGroups={groups}
          onClose={() => { setEditingGroup(null); setCreatingGroup(false); }}
          onSaved={async () => { setEditingGroup(null); setCreatingGroup(false); await loadAll(); await refreshMe(); }}
        />
      )}
      {editingUser && (
        <UserEditor
          modules={modules}
          groups={groups}
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSaved={async () => { setEditingUser(null); await loadAll(); await refreshMe(); }}
        />
      )}
    </div>
  );
}

function PermMatrix({ modules, acoes, setAcoes, disabledKeys = new Set() }) {
  const grupos = [...new Set(Object.values(modules).map(c => c.grupo))];
  const toggle = (key, val) => setAcoes({ ...acoes, [key]: val });
  const toggleModule = (mod, val) => {
    const cfg = modules[mod];
    const next = { ...acoes };
    (cfg?.verbs || []).forEach(v => { next[`${mod}.${v}`] = val; });
    setAcoes(next);
  };
  return (
    <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
      {grupos.map((g) => (
        <div key={g}>
          <div className="text-[11px] uppercase tracking-wide text-slate-500 font-bold mb-1">{GRUPO_LABELS[g] || g}</div>
          <div className="space-y-1">
            {Object.entries(modules).filter(([, c]) => c.grupo === g).map(([mod, cfg]) => {
              const allOn = cfg.verbs.every(v => acoes[`${mod}.${v}`] === true);
              const allOff = cfg.verbs.every(v => !acoes[`${mod}.${v}`]);
              return (
                <div key={mod} className="border rounded p-2 bg-white">
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-semibold text-sm text-slate-800">{cfg.label}</div>
                    <div className="flex gap-1">
                      <button onClick={() => toggleModule(mod, true)} className={`text-[10px] px-2 py-0.5 rounded ${allOn ? 'bg-emerald-200 text-emerald-900' : 'bg-slate-100 hover:bg-emerald-100'}`} data-testid={`perm-mod-all-${mod}`}>Tudo</button>
                      <button onClick={() => toggleModule(mod, false)} className={`text-[10px] px-2 py-0.5 rounded ${allOff ? 'bg-rose-200 text-rose-900' : 'bg-slate-100 hover:bg-rose-100'}`} data-testid={`perm-mod-none-${mod}`}>Nada</button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {cfg.verbs.map((v) => {
                      const key = `${mod}.${v}`;
                      const on = acoes[key] === true;
                      const disabled = disabledKeys.has(key);
                      return (
                        <button key={v} onClick={() => !disabled && toggle(key, !on)} disabled={disabled} className={`text-[11px] px-2 py-1 rounded border ${on ? 'bg-emerald-500 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'} ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`} data-testid={`perm-verb-${mod}-${v}`}>
                          {VERB_LABELS[v] || v}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function GroupEditor({ modules, group, creating, allGroups = [], onClose, onSaved }) {
  const [nome, setNome] = useState(group?.nome || '');
  const [descricao, setDescricao] = useState(group?.descricao || '');
  const [acoes, setAcoes] = useState(group?.acoes_v2 || {});
  const [saving, setSaving] = useState(false);
  const [templateId, setTemplateId] = useState('');
  const isSystem = !!group?.system;

  const aplicarTemplate = () => {
    if (!templateId) { toast.error('Escolha um grupo de origem.'); return; }
    const src = allGroups.find((g) => g.id === templateId);
    if (!src) { toast.error('Grupo não encontrado.'); return; }
    setAcoes({ ...(src.acoes_v2 || {}) });
    toast.success(`Permissões copiadas de "${src.nome}". Revise e salve.`);
  };

  const salvar = async () => {
    if (!nome.trim()) { toast.error('Nome obrigatório.'); return; }
    setSaving(true);
    try {
      if (creating) {
        await api.post('/perm/groups', { nome, descricao, acoes_v2: acoes });
        toast.success('Grupo criado.');
      } else {
        await api.patch(`/perm/groups/${group.id}`, { nome, descricao, acoes_v2: acoes });
        toast.success('Grupo atualizado.');
      }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Falha ao salvar.'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl bg-white">
        <DialogHeader>
          <DialogTitle>{creating ? 'Novo Grupo' : `Editar Grupo · ${group?.nome}`}{isSystem && <span className="ml-2 text-xs px-2 py-0.5 rounded bg-purple-100 text-purple-700">SISTEMA</span>}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Nome</Label><Input value={nome} onChange={(e) => setNome(e.target.value)} data-testid="perm-group-nome" /></div>
          <div><Label>Descrição</Label><Input value={descricao} onChange={(e) => setDescricao(e.target.value)} /></div>
        </div>
        <div className="flex items-end gap-2 bg-slate-50 border rounded p-2">
          <div className="flex-1">
            <Label className="text-xs text-slate-600">Aplicar template de outro grupo</Label>
            <select
              className="border rounded w-full px-2 py-2 text-sm"
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              data-testid="perm-group-template-select"
            >
              <option value="">Selecione um grupo…</option>
              {allGroups.filter((g) => !group || g.id !== group.id).map((g) => (
                <option key={g.id} value={g.id}>{g.nome} ({Object.values(g.acoes_v2 || {}).filter((v) => v).length} perms)</option>
              ))}
            </select>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={aplicarTemplate}
            disabled={!templateId}
            className="border-purple-400 text-purple-700 hover:bg-purple-50"
            data-testid="perm-group-template-apply"
          >
            Aplicar template
          </Button>
        </div>
        <PermMatrix modules={modules} acoes={acoes} setAcoes={setAcoes} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={salvar} disabled={saving} className="bg-purple-600 text-white" data-testid="perm-group-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UserEditor({ modules, groups, user, onClose, onSaved }) {
  const [profileId, setProfileId] = useState(user.custom_profile_id || '');
  const [overrides, setOverrides] = useState((user.permissoes || {}).acoes_v2 || {});
  const [saving, setSaving] = useState(false);

  const grupoAtual = useMemo(() => groups.find(g => g.id === profileId), [groups, profileId]);
  const baseGrupo = grupoAtual?.acoes_v2 || {};
  const efetivo = { ...baseGrupo, ...overrides };
  const isAdmin = user.role === 'admin';

  const setOverride = (next) => {
    // Mantém apenas chaves que divergem do grupo (overrides reais)
    const clean = {};
    Object.keys(next).forEach((k) => {
      const grupoVal = baseGrupo[k] === true;
      const userVal = next[k] === true;
      if (userVal !== grupoVal) clean[k] = userVal;
    });
    setOverrides(clean);
  };

  const salvar = async () => {
    setSaving(true);
    try {
      await api.put(`/perm/users/${user.id}`, {
        custom_profile_id: profileId || '',
        acoes_v2: overrides,
      });
      toast.success('Permissões salvas.');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Falha ao salvar.'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl bg-white">
        <DialogHeader>
          <DialogTitle>Permissões · {user.name} <span className="text-slate-400 text-sm ml-1">{user.email}</span></DialogTitle>
          {isAdmin && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5" />Usuário <strong>ADMIN legado</strong>: bypass total. As permissões individuais são apenas referência (não são aplicadas).
            </div>
          )}
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Grupo (base)</Label>
            <select className="border rounded w-full px-2 py-2 text-sm" value={profileId} onChange={(e) => setProfileId(e.target.value)} data-testid="perm-user-group">
              <option value="">(sem grupo)</option>
              {groups.map((g) => (<option key={g.id} value={g.id}>{g.nome}</option>))}
            </select>
            <div className="text-[11px] text-slate-500 mt-1">{grupoAtual ? `${Object.values(baseGrupo).filter(v => v).length} permissões herdadas` : 'Nenhum grupo: tudo bloqueado por padrão.'}</div>
          </div>
          <div className="text-[11px] text-slate-600 bg-slate-50 border rounded p-2">
            Botão verde = liberado. Botão branco = bloqueado.
            <br />Override individual <strong>sobrescreve o grupo</strong>: marcar/desmarcar aqui diverge do padrão herdado.
          </div>
        </div>
        <PermMatrix modules={modules} acoes={efetivo} setAcoes={setOverride} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={salvar} disabled={saving} className="bg-purple-600 text-white" data-testid="perm-user-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
